import React, { useState, useRef, useEffect } from 'react';
import './TeamHeader.css';
import { useAuth } from '../../Auth';
import { Link } from 'react-router-dom';

//Icons
import { MdOutlineInventory, MdOutlineMyLocation, MdOutlineConstruction, MdMenu } from "react-icons/md";
import { TbAlertSquareRounded } from "react-icons/tb";
import { LuLogs, LuBriefcaseBusiness, LuUserRound } from "react-icons/lu";
import { SlPeople, SlLocationPin } from "react-icons/sl";
import { SiOpensourcehardware } from "react-icons/si";
import { IoHardwareChipOutline } from "react-icons/io5";
import { PiScreencast } from "react-icons/pi";
import { CiServer } from "react-icons/ci";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { RxQuestionMarkCircled } from "react-icons/rx";

//Assets
import fnnLogo from '../../img/FNNLogoWithWhiteFont-07.png'

interface TeamHeaderProps {
    title?: string; // make title optional, fallback to default
}


const TeamHeader: React.FC<TeamHeaderProps> = ({ title = "FNN Team Portal" }) => {

    const { username, first_name, last_name, logout, is_staff } = useAuth();
    const [isLeftHeaderOpen, setIsLeftHeaderOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const toggleDropdown = (name: string) => {
        setOpenDropdown(prev => (prev === name ? null : name));
    };

    const departments = [
        {
            name: 'Hardware',
            icon: <SiOpensourcehardware />,
            options: [
                { label: "Detector Management", path: "/hardware/detector_management" },
                { label: "Hardware History", path: "/hardware/history" }
            ]
        },
        {
            name: 'Server', icon: <CiServer />,
            options: [
                { label: "Server Status", path: "/server/status" }
            ]
        },
        {
            name: 'TOA', icon: <MdOutlineMyLocation />,
            options: [
                { label: "Activity & Performance", path: "/toa/activity_and_performance" },
                { label: "Tools & Repositories", path: "/toa/tools_and_repositories" },
                { label: "Efficiency & Reference Files", path: "/toa/files" }
            ]
        },
        {
            name: 'AI', icon: <IoHardwareChipOutline />,
            options: [
                { label: "Data Management", path: "/ai/data_management" },
                { label: "Request Activity", path: "/ai/request_activity" },
                { label: "Pipeline Overview", path: "/ai/pipeline_overview" }
            ]
        },
        { name: 'GIS', icon: <SlLocationPin />, options: [] },
        {
            name: 'Marketing',
            icon: <PiScreencast />,
            options: [
                { label: "Main Page", path: "/marketing/main" },
                { label: "Customer Directory", path: "/marketing/customers" },
                { label: "Active Products", path: "/marketing/products" }
            ]
        },
        { name: 'Account', icon: <LuBriefcaseBusiness />, options: [] },
        { name: 'Manufacturing', icon: <MdOutlineConstruction />, options: [] },
    ];


    // ---------------------------------------- User menu functionality ----------------------------------------
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const toggleProfileMenu = () => {
        setShowProfileMenu(prev => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setShowProfileMenu(false);
        }
    };

    function logoutAndClosePop() {
        toggleProfileMenu();
        logout();
    }

    useEffect(() => {
        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfileMenu]);
    // ---------------------------------------- User menu functionality ----------------------------------------

    return (
        <div className="team-header">
            <div
                className={`team-header-left ${isLeftHeaderOpen ? 'open' : ''}`}
                onMouseEnter={() => setIsLeftHeaderOpen(true)}
                onMouseLeave={() => (setIsLeftHeaderOpen(false), setOpenDropdown(null))}
            >

                <div className='team-header-menu'>
                    <MdMenu className='team-header-left-icon' />
                </div>

                {/* First List */}
                <div className='team-header-left-list'>
                    <Link className='team-header-left-content' to={"/inventory"}>
                        <MdOutlineInventory className='team-header-left-icon' />
                        <span className={`team-header-left-text ${isLeftHeaderOpen ? '' : 'collapsed'}`}>Inventory</span>

                    </Link>
                    <Link className='team-header-left-content' to={"/alert_hub"}>
                        <TbAlertSquareRounded className='team-header-left-icon' />
                        <span className={`team-header-left-text ${isLeftHeaderOpen ? '' : 'collapsed'}`}>Alerts</span>
                    </Link>
                    <Link className='team-header-left-content' to={"/inventory"}>
                        <LuLogs className='team-header-left-icon' />
                        <span className={`team-header-left-text ${isLeftHeaderOpen ? '' : 'collapsed'}`}>Logs</span>
                    </Link>
                    <Link className='team-header-left-content' to={"/inventory"}>
                        <SlPeople className='team-header-left-icon' />
                        <span className={`team-header-left-text ${isLeftHeaderOpen ? '' : 'collapsed'}`}>Directory</span>
                    </Link>
                </div>

                {/* Second List / Departments */}
                <div className='team-header-left-departments'>
                    {departments.map(dept => (
                        <div key={dept.name}>
                            <div
                                className='team-header-left-content department-btn'
                                onClick={() => toggleDropdown(dept.name)}
                            >
                                {dept.icon && <span className='team-header-left-icon'>{dept.icon}</span>}
                                {isLeftHeaderOpen && <span className='team-header-left-text'>{dept.name}</span>}
                                {isLeftHeaderOpen && (
                                    <span className='dropdown-arrow'>
                                        {openDropdown === dept.name ? <FiChevronUp /> : <FiChevronDown />}
                                    </span>
                                )}
                            </div>
                            {/* Dropdown items */}
                            {isLeftHeaderOpen && (
                                <div className={`dropdown-items ${openDropdown === dept.name ? 'open' : ''}`}>
                                    {dept.options.length > 0 ? (
                                        dept.options.map((opt, idx) => (
                                            <Link key={idx} to={opt.path} className='dropdown-item'>
                                                {opt.label}
                                            </Link>
                                        ))
                                    ) : (
                                        <div className='dropdown-item disabled'>No items</div>
                                    )}
                                </div>
                            )}

                        </div>
                    ))}
                </div>

                <div className='team-header-left-content support-header'>
                    <RxQuestionMarkCircled className='team-header-left-icon' />
                    <span className={`team-header-left-text ${isLeftHeaderOpen ? '' : 'collapsed'}`}>Support </span>

                </div>

            </div>

            {/* User menu */}
            <div className="team-header-top">
                <Link to={"/team_portal"}><img src={fnnLogo} alt="" className="fnn-team-header-logo" /></Link>
                <h1>{title}</h1>

                <div className='user-team-header-logo-wrapper' onClick={toggleProfileMenu}>
                    <LuUserRound className='user-team-header-logo' />
                    <p>{first_name !== "" ? first_name : username.split("@")[0]}</p>
                </div>

            </div>

            {/* User menu dropdown */}
            {showProfileMenu && (
                <div className="logged-user-sidebar" ref={menuRef}>
                    <div>
                        <h1>{first_name !== "" ? first_name : username.split("@")[0]} {last_name}</h1>
                        <p>{username.split('@')[0]}</p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Link
                            to="/team_portal"
                            className="logged-user-sidebar-btn"
                            style={{ textDecoration: 'none', color: 'black' }}
                        >
                            Team Portal
                        </Link>

                        <Link
                            to="/login"
                            className="logged-user-sidebar-btn"
                            style={{ textDecoration: 'none', color: 'black' }}
                        >
                            Customer Portal
                        </Link>

                        {is_staff &&
                            <a
                                href="/admin"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="logged-user-sidebar-btn"
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                Admin Portal
                            </a>
                        }

                        <Link
                            to="/login"
                            className="logged-user-sidebar-btn"
                            onClick={logoutAndClosePop}
                            style={{ textDecoration: 'none', color: 'black' }}
                        >
                            Log Out
                        </Link>


                    </div>
                </div>
            )}


        </div>
    );
};

export default TeamHeader;
